import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ProductForm from "./ProductForm";
import ProductVariantForm from "./OptionalForm";
import { addProduct } from "../../api/productService";
import { getErrorMessage } from "../../utils/errorHandler";
import { addSeo } from "../../api/SeoService";
import SeoFields from "../../common/SEOFields";

export default function AddProductStepper() {
  const navigate = useNavigate();
  // STEP (persisted)
  const [step, setStep] = useState(() => {
    return Number(localStorage.getItem("productStep")) || 1;
  });
  // PRODUCT ID (persisted)
  const [productId, setProductId] = useState(() => {
    return localStorage.getItem("productId") || "";
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [backendErrors, setBackendErrors] = useState({});
  const [seoLoading, setSeoLoading] = useState(false);

  const [seoFormData, setSeoFormData] = useState({
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    metaImage: null,
    metaImagePreview: "",
  });

  const [seoErrors, setSeoErrors] = useState({});
  const [seoImages, setSeoImages] = useState([]);

  useEffect(() => {
    if (productId) {
      localStorage.setItem("productStep", step);
    }
  }, [step, productId]);
  useEffect(() => {
    if (productId) {
      localStorage.setItem("productId", productId);
    }
  }, [productId]);
  const handleAddProduct = async (formData) => {
    try {
      setLoading(true);
      setBackendErrors({});

      // =====================================================
      // 1. ADD PRODUCT
      // =====================================================
      const response = await addProduct(formData);

      if (response?.status === 422) {
        setBackendErrors(response.error || {});
        return;
      }

      if (!response?.success) {
        alert(response?.message || "Failed to create product");
        return;
      }

      const createdId = response?.data;

      if (!createdId) {
        alert("Product created but product ID was not returned.");
        return;
      }

      // =====================================================
      // SAVE PRODUCT ID
      // =====================================================
      setProductId(createdId);
      localStorage.setItem("productId", createdId);

      // =====================================================
      // PRE-FILL SEO DATA
      // =====================================================
      setSeoFormData({
        metaTitle: formData.metaTitle || formData.productName || "",

        metaDescription:
          formData.metaDescription || formData.productDescription || "",

        metaKeywords: Array.isArray(formData.metaKeywords)
          ? formData.metaKeywords.join(", ")
          : formData.metaKeywords || "",

        metaImage: formData.metaImage || null,
        metaImagePreview: "",
      });

      // =====================================================
      // MOVE TO STEP 2
      // =====================================================
      setStep(2);
      localStorage.setItem("productStep", "2");
    } catch (error) {
      console.error("Add Product Error:", error);
      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };
  const handleAddSeo = async (e) => {
    e.preventDefault();

    try {
      setSeoLoading(true);
      setSeoErrors({});

      if (!productId) {
        alert("Product ID is missing.");
        return;
      }

      // Convert Meta Image File to Base64 string
      let metaImage = "";

      if (seoFormData.metaImage instanceof File) {
        metaImage = await fileToBase64(seoFormData.metaImage);
      } else if (typeof seoFormData.metaImage === "string") {
        metaImage = seoFormData.metaImage;
      }

      const payload = {
        referenceId: String(productId),
        referenceType: "Product",

        metaTitle: seoFormData.metaTitle?.trim() || "",

        metaDescription: seoFormData.metaDescription?.trim() || "",

        metaKeywords: seoFormData.metaKeywords
          ? seoFormData.metaKeywords
              .split(",")
              .map((keyword) => keyword.trim())
              .filter(Boolean)
          : [],

        metaImage: metaImage,

        images: seoImages.map((image) => image.data),
      };

      console.log("SEO PAYLOAD:", payload);

      const response = await addSeo(payload);

      if (response?.status === 422) {
        setSeoErrors(response.error || {});
        return;
      }

      if (!response?.success) {
        alert(response?.message || "SEO failed");
        return;
      }

      setStep(3);
      localStorage.setItem("productStep", "3");
    } catch (error) {
      console.error("SEO ERROR:", error);
      alert(getErrorMessage(error));
    } finally {
      setSeoLoading(false);
    }
  };
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };
  const handleSeoImagesChange = async (e) => {
    const files = Array.from(e.target.files || []);

    if (files.length > 5) {
      alert("You can select maximum 5 images.");
      e.target.value = "";
      return;
    }

    try {
      const base64Images = await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          type: file.type,
          data: await fileToBase64(file),
        })),
      );

      setSeoImages(base64Images);
    } catch (error) {
      console.error("Image conversion error:", error);
    }
  };
  const handleSeoChange = (e) => {
    const { name, value } = e.target;

    setSeoFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setSeoErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };
  const handleSeoImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setSeoFormData((prev) => ({
      ...prev,
      metaImage: file,
      metaImagePreview: URL.createObjectURL(file),
    }));
  };
  const handleVariantSuccess = () => {
    setSuccess(true);
    // STOP saving effect first
    setProductId("");
    // CLEAR STORAGE
    localStorage.removeItem("productStep");
    localStorage.removeItem("productId");
    localStorage.removeItem("productForm");
    setStep(1);
    setTimeout(() => {
      navigate("/products");
    }, 2000);
  };
  return (
    <div className="container-fluid p-4">
      <div className="card border-2">
        <div className="card-body">
          
          {/* STEPPER */}
          <div className="d-flex justify-content-between align-items-center mb-3 position-relative">
            <div
              style={{
                position: "absolute",
                top: "22px",
                left: "8%",
                width: "82%",
                height: "4px",
                background: "#dee2e6",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  width: step === 1 ? "0%" : step === 2 ? "50%" : "100%",
                  height: "100%",
                  background: "#59b71f",
                  transition: "0.3s",
                }}
              />
            </div>
            {/* STEP 1 */}
            <div
              className="text-center position-relative"
              style={{ zIndex: 2 }}
            >
              <div
                style={{
                  width: "45px",
                  height: "45px",
                  borderRadius: "50%",
                  background: step >= 1 ? "#59b71f" : "#dee2e6",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "18px",
                  margin: "0 auto",
                }}
              >
                1
              </div>
              <p className="mt-2 mb-0">Product Details</p>
            </div>
            {/* STEP 2 */}
            <div
              className="text-center position-relative"
              style={{ zIndex: 2 }}
            >
              <div
                style={{
                  width: "45px",
                  height: "45px",
                  borderRadius: "50%",
                  background: step >= 2 ? "#59b71f" : "#dee2e6",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "18px",
                  margin: "0 auto",
                }}
              >
                2
              </div>
              <p className="mt-2 mb-0">SEO Data</p>
            </div>
            {/* STEP 3 */}
            <div
              className="text-center position-relative"
              style={{ zIndex: 2 }}
            >
              <div
                style={{
                  width: "45px",
                  height: "45px",
                  borderRadius: "50%",
                  background: step >= 3 ? "#59b71f" : "#dee2e6",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "18px",
                  margin: "0 auto",
                }}
              >
                3
              </div>
              <p className="mt-2 mb-0">Product Variations</p>
            </div>
          </div>
          {/* STEP 1 */}
          {step === 1 && (
            <>
              <h5 className="mb-3">Product Details</h5>
              <ProductForm
                onSubmit={handleAddProduct}
                loading={loading}
                backendErrors={backendErrors}
              />
            </>
          )}
          {step === 2 && (
            <>
              <h5 className="mb-3">SEO Details</h5>

              <form onSubmit={handleAddSeo}>
                <SeoFields
                  formData={seoFormData}
                  setFormData={setSeoFormData}
                  handleChange={handleSeoChange}
                  errors={seoErrors}
                  backendErrors={seoErrors}
                  handleImageChange={handleSeoImageChange}
                />
                <div className="row mt-3">
                  <div className="col-md-12">
                    <label className="form-label fw-semibold">
                      SEO Images
                      <span className="text-muted"> (Maximum 5)</span>
                    </label>

                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      multiple
                      onChange={handleSeoImagesChange}
                    />

                    <small className="text-muted">
                      You can select maximum 5 images.
                    </small>

                    {seoImages.length > 0 && (
                      <div className="mt-3 d-flex flex-wrap gap-3">
                        {seoImages.map((file, index) => (
                          <div
                            key={`${file.name}-${index}`}
                            className="position-relative"
                            style={{
                              width: "120px",
                              height: "120px",
                            }}
                          >
                            <img
                              src={file.data}
                              alt={`SEO ${index + 1}`}
                              className="img-thumbnail"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={seoLoading}
                  >
                    {seoLoading ? "Saving..." : "Save SEO & Continue"}
                  </button>
                </div>
              </form>
            </>
          )}
          {/* STEP 3 */}
          {step === 3 && !success && (
            <>
              <h5 className="mb-3">Add Product Variations</h5>
              <ProductVariantForm
                id={productId}
                onSuccess={handleVariantSuccess}
              />
            </>
          )}
          {/* SUCCESS */}
          {success && (
            <div className="alert alert-success text-center my-4">
              Product added successfully! Redirecting...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
